import { type ReactNode, useMemo } from "react";

import clsx from "clsx";
import moment from "moment";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  type DateRangeChoiceV4,
  type FixedDateInterval,
} from "@north-beam/nb-common";

import { InputButton } from "../inputs/input-button";

import { QUICK_SELECT_OPTIONS } from "./constants";
import { DatePickerPanel } from "./date-picker-panel";

interface Props {
  value: FixedDateInterval | DateRangeChoiceV4 | null;
  minDate?: string;
  maxDate?: string;
  placeholder?: ReactNode;
  disabled?: boolean;
  clearable?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  onChange: (value: FixedDateInterval | DateRangeChoiceV4 | null) => void;
}

export const DatePicker = ({
  value,
  minDate,
  maxDate = moment().format("YYYY-MM-DD"),
  placeholder = "Select dates",
  disabled,
  clearable,
  size = "md",
  className,
  onChange,
}: Props) => {
  const label = useMemo(() => {
    if (!value) {
      return null;
    }

    if (typeof value === "string") {
      return QUICK_SELECT_OPTIONS.find((e) => e.value === value)?.label;
    }

    return "Custom";
  }, [value]);

  return (
    <Popover>
      <PopoverButton
        as={InputButton}
        size={size}
        disabled={disabled}
        rightIcon={
          clearable && value ? (
            <i
              className={clsx(
                "fa-regular fa-times-circle text-sm text-v2-cosmic/400 cursor-pointer",
                "hover:text-v2-cosmic/600 group-data-[disabled]:text-v2-gray/200",
              )}
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            />
          ) : (
            <i className="fa-regular fa-calendar" />
          )
        }
        className={clsx(className, {
          "text-v2-secondary focus:text-v2-secondary": !value,
        })}
      >
        {label ?? placeholder}
      </PopoverButton>
      <PopoverPanel
        as="div"
        anchor={{ to: "bottom start", gap: 4 }}
        transition
        className={clsx(
          "z-[9999] border border-v2-secondary rounded-md elevation-2 bg-v2-primary",
          "origin-top transition duration-200 ease-out",
          "data-[closed]:translate-y-1 data-[closed]:opacity-0",
        )}
      >
        {({ close }) => {
          return (
            <DatePickerPanel
              value={value}
              minDate={minDate}
              maxDate={maxDate}
              onChange={onChange}
              close={close}
            />
          );
        }}
      </PopoverPanel>
    </Popover>
  );
};
